/**
 * Mis datos: contacto y contraseña.
 *
 * Solo se deja tocar por dónde localizarle. El nombre, el documento y la fecha
 * de nacimiento son identidad y se corrigen en admisión con el documento
 * delante: si el paciente pudiera editarlos, su historia clínica dejaría de
 * poder cruzarse con la persona real.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { Mail, Phone, ShieldCheck } from "lucide-react";
import { actualizarMisDatos } from "../../api/portal";
import { usePortalAuth } from "../../auth/PortalAuthProvider";
import { formatApiError } from "../../api/client";
import { TelefonoInput } from "../../components/TelefonoInput";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
} from "../../components/ui";
import { CambiarPasswordCard } from "./CambiarPasswordCard";

export function PortalMisDatosPage() {
  const { me, recargar } = usePortalAuth();
  const [email, setEmail] = useState("");
  const [telefono, setTelefono] = useState("");
  const [ok, setOk] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEmail(me?.yo.email ?? "");
    setTelefono(me?.yo.telefono ?? "");
  }, [me?.yo.email, me?.yo.telefono]);

  const guardar = useMutation({
    mutationFn: () =>
      actualizarMisDatos({
        email: email.trim() || null,
        telefono: telefono.trim() || null,
      }),
    onSuccess: async () => {
      setOk(true);
      setError(null);
      await recargar();
    },
    onError: (e) => setError(formatApiError(e, "No se pudieron guardar tus datos.")),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setOk(false);
    guardar.mutate();
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Mis datos</h1>
        <p className="mt-0.5 text-sm text-slate-500">
          Por dónde localizarte y tu contraseña.
        </p>
      </div>

      <Card>
        <CardHeader>
          <h2 className="font-semibold text-slate-900">Cómo contactarte</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Con tu correo podrás recuperar la contraseña si la olvidas.
          </p>
        </CardHeader>
        <CardBody>
          <form onSubmit={onSubmit} className="space-y-4">
            {error && <Alert>{error}</Alert>}
            {ok && <Alert variant="success">Datos guardados.</Alert>}

            <Field label="Correo electrónico" htmlFor="md_email">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 shrink-0 text-slate-400" />
                <Input
                  id="md_email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tucorreo@gmail.com"
                />
              </div>
            </Field>

            <Field label="Teléfono" htmlFor="md_tel">
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4 shrink-0 text-slate-400" />
                <TelefonoInput value={telefono} onChange={setTelefono} />
              </div>
            </Field>

            {/* Se enseña lo que NO se puede tocar, para que nadie lo busque. */}
            <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
              <p>
                <span className="text-slate-500">Nombre:</span>{" "}
                {me?.yo.nombre_completo}
              </p>
              <p>
                <span className="text-slate-500">Documento:</span>{" "}
                {me?.yo.tipo_documento} {me?.yo.num_documento}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Si algo de esto está mal, corrígelo en admisión con tu documento.
              </p>
            </div>

            <div className="flex justify-end">
              <Button type="submit" loading={guardar.isPending}>
                Guardar
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      {/* Si su clave sigue siendo el documento, esto es lo más importante de
          la pantalla y va con su propio aviso. */}
      {me?.yo.num_documento && (
        <Alert variant="info">
          <span className="inline-flex items-start gap-2">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Si tu contraseña sigue siendo tu DNI, cámbiala. Tu documento lo
              conoce mucha gente —está en recibos y trámites— y aquí se ven tus
              resultados y tus recetas.
            </span>
          </span>
        </Alert>
      )}

      <CambiarPasswordCard />
    </div>
  );
}
